import { apiClient } from '../client';
import type { Paginated, PaginationQuery } from './types';

export interface Activity {
  id: number;
  accountId: number | null;
  personId: number | null;
  projectId: number | null;
  saleId: number | null;
  accountNameSnapshot: string | null;
  personNameSnapshot: string | null;
  projectNameSnapshot: string | null;
  startDate: string | null;
  endDate: string | null;
  type: string | null;
  closed: boolean;
  private: boolean;
  showInCalendar: boolean;
  assignedToId: number | null;
  assignedById: number | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
  version: number;
  account?: { id: number; name: string } | null;
  person?: { id: number; firstName: string | null; lastName: string } | null;
  project?: { id: number; name: string } | null;
  assignedTo?: { id: number; firstName: string; lastName: string } | null;
}

export type ActivityListItem = Pick<
  Activity,
  | 'id'
  | 'type'
  | 'notes'
  | 'startDate'
  | 'endDate'
  | 'closed'
  | 'accountNameSnapshot'
  | 'personNameSnapshot'
  | 'account'
  | 'person'
  | 'assignedTo'
>;

export type ActivityQuickFilter = 'mine' | 'overdue' | 'today' | 'week';

export interface ActivityListQuery extends PaginationQuery {
  accountId?: number;
  personId?: number;
  projectId?: number;
  assignedToId?: number;
  closed?: boolean;
  type?: string;
  startFrom?: string;
  startTo?: string;
  quick?: ActivityQuickFilter;
  keywordIds?: string;
}

export type ActivityInput = Partial<Omit<Activity, 'id' | 'createdAt' | 'updatedAt' | 'version'>> & {
  notes: string;
};

export interface CalendarActivity {
  id: number;
  notes: string;
  type: string | null;
  startDate: string | null;
  endDate: string | null;
  closed: boolean;
  assignedToId: number | null;
  account: { id: number; name: string } | null;
}

export interface RemindersResponse {
  overdue: ActivityListItem[];
  today: ActivityListItem[];
  overdueCount: number;
  todayCount: number;
}

export const activitiesApi = {
  async list(q: ActivityListQuery): Promise<Paginated<ActivityListItem>> {
    const { data } = await apiClient.get<Paginated<ActivityListItem>>('/activities', { params: q });
    return data;
  },
  async findOne(id: number): Promise<Activity> {
    const { data } = await apiClient.get<Activity>(`/activities/${id}`);
    return data;
  },
  async create(input: ActivityInput): Promise<Activity> {
    const { data } = await apiClient.post<Activity>('/activities', input);
    return data;
  },
  async update(id: number, input: Partial<ActivityInput>): Promise<Activity> {
    const { data } = await apiClient.patch<Activity>(`/activities/${id}`, input);
    return data;
  },
  async remove(id: number): Promise<void> {
    await apiClient.delete(`/activities/${id}`);
  },
  async setClosed(id: number, closed: boolean): Promise<Activity> {
    const { data } = await apiClient.patch<Activity>(`/activities/${id}/close`, { closed });
    return data;
  },
  async calendar(params: { from: string; to: string; mine?: boolean }): Promise<CalendarActivity[]> {
    const { data } = await apiClient.get<CalendarActivity[]>('/activities/calendar', {
      params: { from: params.from, to: params.to, mine: params.mine ? 'true' : undefined },
    });
    return data;
  },
  async reminders(): Promise<RemindersResponse> {
    const { data } = await apiClient.get<RemindersResponse>('/activities/reminders');
    return data;
  },
};
